// ADR-131 D5: the upstream feed record behind one discrepancy row, as received
// on the audit day — formatted and collapsible.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Typography, Box, Stack,
} from '@mui/material';
import directManagerDiscrepanciesApi from '../../../services/api/directManagerDiscrepanciesApi';
import JsonViewer from '../../common/JsonViewer';
import LoadingSpinner from '../../common/LoadingSpinner';

// Human labels for the feed sections (ADR-131 D1 payload shape).
const FEED_LABELS = {
  View_PracStruktura: 'Enova — PowerBI View_PracStruktura (one entry per project row)',
  mysmEmployee: 'Americas — MySM employee',
  sowContracts: 'Americas — SOW contracts',
};

const SourceRecordDialog = ({ row, onClose }) => {
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!row?.id) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setRecord(null);
    directManagerDiscrepanciesApi.getSourceRecord(row.id)
      .then((data) => { if (!cancelled) setRecord(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load the source record'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [row?.id]);

  const feeds = record?.payload?.feeds && typeof record.payload.feeds === 'object' ? record.payload.feeds : null;

  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Source record
        {row?.employee?.fullName && (
          <Typography variant="body2" sx={{ color: '#64748B' }}>{row.employee.fullName}</Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <LoadingSpinner />}
        {error && <Alert severity="error">{error}</Alert>}
        {record && (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: '#475569' }}>
              {`${record.sourceSystem} feed, fetched ${new Date(record.fetchedAt).toLocaleString()}. `}
              Shown exactly as the feed returned it on the audit day, not today&apos;s data.
            </Typography>
            {record.payload?.truncated && (
              <Alert severity="warning">This record exceeded the size limit and was not stored in full.</Alert>
            )}
            {feeds ? Object.entries(feeds).map(([feedName, rows]) => (
              <Box key={feedName}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {FEED_LABELS[feedName] || feedName}
                  {Array.isArray(rows) && ` · ${rows.length} ${rows.length === 1 ? 'record' : 'records'}`}
                </Typography>
                <JsonViewer value={rows} defaultExpandDepth={2} />
              </Box>
            )) : <JsonViewer value={record.payload} />}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SourceRecordDialog;
